import Button from '../Input2/Button';
import { Media } from '../StrokeIcons';

/** Where a voicemail drop stands on the live call. */
export type VoicemailDropState = 'idle' | 'dropping' | 'error';

/**
 * Everything the dialer needs to offer "Leave voicemail" on the live call. The surface passes this
 * only when the server allowed a drop on THIS call (team flag, Twilio, the rep's recorded message,
 * the landline-only rule) — absent = the button doesn't exist, per the feature-flag rule.
 */
export interface VoicemailDropControl {
  state: VoicemailDropState;
  /** Ask the server to play the rep's message and hang up; the surface then moves on. */
  onDrop: () => void;
}

/**
 * The in-call "Leave voicemail" button. The rep clicks it once they hear the beep: the server plays
 * their recorded message to the prospect and hangs up, and the dialer moves on. Disabled while the
 * request is in flight so a double click can't fire twice (the server is idempotent as well).
 */
export function VoicemailDropButton({ control }: { control: VoicemailDropControl }) {
  const dropping = control.state === 'dropping';
  return (
    <div className="flex flex-col items-center gap-1" data-test="voicemail-drop">
      <Button
        color="purple"
        tone="outline"
        icon={<Media.Voicemail />}
        status={dropping ? 'updating' : undefined}
        disabled={dropping}
        tooltip="After the beep: plays your recorded message, hangs up, and moves on"
        onClick={control.onDrop}
        data-test="voicemail-drop-button">
        {dropping ? 'Leaving voicemail…' : 'Leave voicemail'}
      </Button>
      {control.state === 'error' && (
        <p className="text-center text-xs text-red-500" data-test="voicemail-drop-error">
          Couldn’t leave the voicemail — the call is still live. Try again or hang up.
        </p>
      )}
    </div>
  );
}
